import { EntityId, EntityName, ErrorKey } from "@inrev/common";
import { ACTIVE_AGENT_FOLLOWER_REQUIRED } from "@portal/api/followers/hasActiveAgentFollower";
import * as Sentry from "@sentry/react";
import { type QueryClient, type QueryKey } from "@tanstack/react-query";
import { logDataToSentry } from "./sentry";
import { getUploadErrorMessage, isNetworkError, UploadErrorCode } from "./upload-error-messages";

type ApiErrorResponse = {
	name: string;
	message: string;
	path: string;
	statusCode: number;
	timestamp: string;
	key?: ErrorKey;
	entityId?: EntityId | EntityName;
	details?: unknown;
};

export class ApiError extends Error {
	path: string;
	statusCode: number;
	timestamp: string;
	key?: ErrorKey;
	entityId?: EntityId | EntityName;
	details?: unknown;
	requestUrl: string;
	requestMethod: string;
	requestBody?: any;

	constructor(
		apiError: ApiErrorResponse,
		requestContext: {
			url: string;
			method: string;
			body?: any;
		},
	) {
		super(apiError.message);
		this.name = apiError.name;
		this.path = apiError.path;
		this.key = apiError.key;
		this.entityId = apiError.entityId;
		this.details = apiError.details;
		this.statusCode = apiError.statusCode;
		this.timestamp = apiError.timestamp;
		this.requestUrl = requestContext.url;
		this.requestMethod = requestContext.method;
		this.requestBody = requestContext.body;

		// Manually fix the prototype chain to ensure `instanceof` checks and custom methods work correctly
		Object.setPrototypeOf(this, ApiError.prototype);
	}
}

export type Severity = "error" | "warn" | "info";
export type ErrorMessage = {
	header: string;
	description: string;
	severity: Severity;
	onClose?: () => void;
};

const defaultErrorMessage: ErrorMessage = {
	header: "Something went wrong",
	description: `An inRev engineer has been notified of your issue and is investigating. In some cases, it's useful to refresh the page and try again. \n\n Apologies for the inconvenience.`,
	severity: "error",
};

export const errorMessageMap: Record<ErrorKey, ErrorMessage> = {
	max_file_size: getUploadErrorMessage(UploadErrorCode.fileTooLarge),
	file_is_encrypted: getUploadErrorMessage(UploadErrorCode.fileIsEncrypted),
	broker_of_record_account: {
		header: "Permissions error",
		description: "You are not the broker of record for this account.",
		severity: "warn",
	},
	broker_of_record_bond: {
		header: "Permissions error",
		description: "You are not the broker of record for this bond.",
		severity: "warn",
	},
	broker_of_record_quote: {
		header: "Permissions error",
		description: "You are not the broker of record for this bond request.",
		severity: "warn",
	},
	indemnity_individual_exists: {
		header: "Invalid operation",
		description:
			"Individual is on a completed indemnity agreement and cannot be removed at this time.",
		severity: "info",
	},
	indemnity_company_exists: {
		header: "Invalid operation",
		description:
			"Company is on a completed indemnity agreement and cannot be removed at this time.",
		severity: "info",
	},
	indemnity_exists: {
		header: "The indemnity agreement already exists",
		description:
			"The indemnity agreement was already created by another user. Please refresh your page to view the updated details.",
		severity: "info",
	},
	attachment_deleted: {
		header: "Attachment No Longer Exists",
		description: "This attachment has been deleted by another user",
		severity: "warn",
	},
	user_exists: {
		header: "User already exists",
		description: "A user with this email already exists. Please use a different email.",
		severity: "warn",
	},
	model_downsized: {
		header: "Cannot lower bonding needs",
		description:
			"This account's bonding needs were raised by another user and cannot be lowered. Please refresh your page to view the updated details.",
		severity: "warn",
	},
	underwriter_lock: {
		header: "Locked by Underwriter",
		description: "This item has been locked by an underwriter and cannot be edited.",
		severity: "warn",
	},
	bond_request_already_declined: {
		header: "Invalid operation",
		description: "This bond request has already been declined.",
		severity: "warn",
	},
	bond_request_already_in_review: {
		header: "Invalid operation",
		description: "This bond request has already been put in review.",
		severity: "warn",
	},
	bond_request_already_closed: {
		header: "Invalid operation",
		description: "This bond request has already been closed.",
		severity: "warn",
	},
	bond_request_already_written: {
		header: "Bond issued",
		description: "This bond has already been issued. You have been redirected to the bond.",
		severity: "info",
	},
	bond_issuance_already_exists: {
		header: "Issuance already started",
		description:
			"A bond issuance has already been started for this request. Refresh the page to see its current status.",
		severity: "info",
	},
	bond_request_already_open: {
		header: "Invalid operation",
		description: "This bond request is already open.",
		severity: "warn",
	},
	final_bond_already_requested: {
		header: "Final bond already requested",
		description:
			"A final bond request already exists for this bid. View the existing request for its status.",
		severity: "warn",
	},
	bond_number_exists: {
		header: "Duplicate Bond Number",
		description:
			"This bond number has already been used. Please try again with a different bond number.",
		severity: "error",
	},
	account_already_closed: {
		header: "Invalid operation",
		description: "This account has already been closed.",
		severity: "warn",
	},
	account_already_open: {
		header: "Invalid operation",
		description: "This account is already open.",
		severity: "warn",
	},
	tag_display_name_conflict: {
		header: "Tag name already exists",
		description: "A tag with this name already exists. Please create a unique name for tags",
		severity: "warn",
	},
	tag_not_found: {
		header: "Tag not found",
		description: "This tag was deleted. Please refresh the page for updated tag information",
		severity: "warn",
	},
	entity_not_found: {
		header: "Not found",
		description: "This entity was deleted. Please refresh the page for updated information",
		severity: "warn",
	},
	bonding_line_placement: {
		header: "Bonding Line Placement",
		description:
			"This update would cause the carrier's single bond or aggregate bonding limit to be exceeded. The bond request has not been saved.",
		severity: "warn",
	},
	"account.conflict.stale_version": {
		header: "Account updated by another user",
		description:
			"This account was updated while you were editing. Clicking OK will close the form and load the latest changes.",
		severity: "warn",
	},
	"bond.conflict.stale_version": {
		header: "Bond updated by another user",
		description:
			"This bond was updated while you were editing. Clicking OK will close the form and load the latest changes.",
		severity: "warn",
	},
	"quote.conflict.stale_version": {
		header: "Bond request updated by another user",
		description:
			"This bond request was updated while you were editing. Clicking OK will close the form and load the latest changes.",
		severity: "warn",
	},
	bond_numbers_exhausted: {
		header: "Bond Numbers Exhausted",
		description:
			"Apologies. We're updating your bond number roll. An underwriter will reach out to you shortly. Thanks for your patience.",
		severity: "warn",
	},
	active_agent_follower_required: {
		header: "Invalid operation",
		description: ACTIVE_AGENT_FOLLOWER_REQUIRED,
		severity: "warn",
	},
	wip_line_item_project_conflict: {
		header: "Project already assigned",
		description: "Another line item on this WIP schedule is already assigned to that project.",
		severity: "warn",
	},
	wip_extraction_review_blocked: {
		header: "Review incomplete",
		description:
			"This WIP schedule has issues that need to be resolved before it can be marked reviewed.",
		severity: "warn",
	},
	validation_failed: defaultErrorMessage,
	limits_required: defaultErrorMessage,
	line_exceeds_limits: defaultErrorMessage,
	limits_invalid: defaultErrorMessage,
	bonding_lines_stale: defaultErrorMessage,
	bonding_lines_not_publishable: defaultErrorMessage,
	bonding_line_exists: defaultErrorMessage,
	default: defaultErrorMessage,
};

export const createVersionConflictHandler = (
	queryKeys: QueryKey[],
	queryClient: QueryClient,
	triggerErrorModal: (err: Error, params?: Partial<ErrorMessage>) => void,
	options?: {
		refetchType?: "active" | "inactive" | "all" | "none";
		onConflict?: () => void;
	},
) => {
	return (err: Error) => {
		if (err instanceof ApiError && err.key?.endsWith(".conflict.stale_version")) {
			queryKeys.forEach((queryKey) =>
				queryClient.invalidateQueries({ queryKey, refetchType: options?.refetchType ?? "none" }),
			);
			triggerErrorModal(err, { onClose: options?.onConflict });
		} else {
			logError(err);
			triggerErrorModal(err);
		}
	};
};

export const isApiError = (err: unknown): err is ApiError => {
	return err instanceof ApiError;
};

/** Resolve the user-facing modal copy for an error, including network failures. */
export const resolveErrorMessage = (err: Error): ErrorMessage => {
	if (isNetworkError(err)) {
		return getUploadErrorMessage(UploadErrorCode.network);
	}
	if (isApiError(err)) {
		return errorMessageMap[err.key ?? ErrorKey.default];
	}
	return errorMessageMap.default;
};

export const isStaleVersionConflict = (err: unknown): boolean =>
	isApiError(err) && (err.key?.endsWith(".conflict.stale_version") ?? false);

export const isNotFoundError = (err: unknown): boolean => isApiError(err) && err.statusCode === 404;

export const isAuthenticationError = (err: unknown): boolean => {
	if (err instanceof ApiError) {
		return err.statusCode === 401;
	}

	return false;
};

export const logError = (err: Error) => {
	if (process.env.VITE_SENTRY_ENV !== "prod") {
		console.error(err);
	}

	// Don't log authentication errors to Sentry
	if (isAuthenticationError(err)) {
		console.error("User is not authenticated", err);
		return;
	}

	// API 500+ errors are captured by backend GCP monitoring with full request context.
	// Skip Sentry to avoid double-alerting.
	if (isApiError(err) && err.statusCode >= 500) {
		return;
	}

	// This will create an info log in Sentry for the failed to fetch error
	if (isNetworkError(err)) {
		logDataToSentry(err.name, {
			message: err.message,
			stack: err.stack,
			userOnline: navigator.onLine,
			...(isApiError(err)
				? {
						statusCode: err.statusCode,
						requestUrl: err.requestUrl,
						requestMethod: err.requestMethod,
					}
				: {}),
		});
		return;
	}

	// Check to see if we should skip logging for this error based on its severity
	const { severity } = errorMessageMap[(err as ApiError).key ?? "default"];

	// If the severity is defined and is not "error", skip logging
	if (severity !== "error") {
		return;
	}

	// Log the error to Sentry with additional context if it's an ApiError
	Sentry.withScope((scope) => {
		if (isApiError(err)) {
			scope.setTransactionName(`Request Error: ${err.statusCode}`);
			scope.setTag("status_code", err.statusCode);
			scope.setTag("key", err.key ?? "Unknown");
		}

		scope.setTag("error_name", err?.name);
		scope.captureException(err);
	});
};

export const normalizeToError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

export const reportReactRootError = (
	error: unknown,
	errorInfo: { componentStack?: string | null },
) => {
	const err = normalizeToError(error);
	Sentry.withScope((scope) => {
		if (errorInfo.componentStack) {
			scope.setExtra("componentStack", errorInfo.componentStack);
		}
		logError(err);
	});
};
